(() => {
  const initializedSections = new WeakSet();

  function toInteger(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.round(parsed) : 0;
  }

  function getMoneyFormat() {
    return window.Shopify && window.Shopify.money_format ? window.Shopify.money_format : '${{amount}}';
  }

  function formatMoney(cents) {
    const amount = toInteger(cents);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(amount, getMoneyFormat());
    }
    return (amount / 100).toFixed(2);
  }

  function getRootUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  async function parseResponseBody(response) {
    const text = await response.text();
    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  async function fetchCartState() {
    try {
      const response = await fetch(`${getRootUrl()}cart.json`, {
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) return null;
      return await parseResponseBody(response);
    } catch (error) {
      return null;
    }
  }

  function getVariantQtyMapFromCart(cart) {
    const map = {};
    if (!cart || !Array.isArray(cart.items)) return map;

    cart.items.forEach((item) => {
      const variantId = toInteger(item && item.id);
      const quantity = toInteger(item && item.quantity);
      if (variantId > 0) {
        map[variantId] = (map[variantId] || 0) + Math.max(quantity, 0);
      }
    });

    return map;
  }

  function getMissingItemsFromCartDiff(requestedItems, beforeMap, afterMap) {
    return requestedItems
      .filter((item) => {
        const variantId = toInteger(item.id);
        const requestedQty = toInteger(item.quantity);
        const beforeQty = toInteger(beforeMap[variantId] || 0);
        const afterQty = toInteger(afterMap[variantId] || 0);
        const addedQty = Math.max(afterQty - beforeQty, 0);
        return variantId > 0 && requestedQty - addedQty > 0;
      })
      .map((item) => {
        const variantId = toInteger(item.id);
        const requestedQty = toInteger(item.quantity);
        const beforeQty = toInteger(beforeMap[variantId] || 0);
        const afterQty = toInteger(afterMap[variantId] || 0);
        const addedQty = Math.max(afterQty - beforeQty, 0);

        return {
          id: variantId,
          quantity: requestedQty - addedQty
        };
      });
  }

  async function requestCartAdd(body) {
    const maxRetries = 2;
    let attempt = 0;

    while (attempt <= maxRetries) {
      const response = await fetch(`${getRootUrl()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      });

      const rawText = await response.text();
      let payload = null;

      if (rawText) {
        try {
          payload = JSON.parse(rawText);
        } catch (error) {
          payload = null;
        }
      }

      if ((response.status === 409 || response.status === 429) && attempt < maxRetries) {
        await new Promise((resolve) => {
          setTimeout(resolve, 200 * (attempt + 1));
        });
        attempt += 1;
        continue;
      }

      return { response, payload, rawText };
    }

    return {
      response: { ok: false, status: 409 },
      payload: null,
      rawText: ''
    };
  }

  function getCartErrorMessage(payload, rawText, statusCode) {
    if (payload && (payload.description || payload.message)) {
      return payload.description || payload.message;
    }

    if (statusCode === 422) {
      return 'One or more selected products are unavailable.';
    }

    const plainText = (rawText || '').trim();
    if (plainText && plainText.indexOf('<') === -1 && plainText.length <= 180) {
      return plainText;
    }

    return 'Unable to add selected products.';
  }

  async function addItemsOneByOne(items) {
    const addedItems = [];
    let lastErrorMessage = '';

    for (const item of items) {
      const result = await requestCartAdd({
        id: item.id,
        quantity: item.quantity
      });

      if (result.response.ok) {
        addedItems.push(result.payload || item);
      } else if (!lastErrorMessage) {
        lastErrorMessage = getCartErrorMessage(result.payload, result.rawText, result.response.status);
      }
    }

    return {
      addedItems,
      lastErrorMessage
    };
  }

  async function ensureItemsInCart(items, beforeQtyMap) {
    let lastErrorMessage = '';
    const maxRounds = 3;

    for (let round = 0; round < maxRounds; round += 1) {
      const currentCart = await fetchCartState();
      if (!currentCart) {
        lastErrorMessage = lastErrorMessage || 'Unable to verify cart contents.';
        continue;
      }

      const currentQtyMap = getVariantQtyMapFromCart(currentCart);
      const missingItems = getMissingItemsFromCartDiff(items, beforeQtyMap, currentQtyMap);

      if (!missingItems.length) {
        return {
          complete: true,
          lastErrorMessage
        };
      }

      const repairResult = await addItemsOneByOne(missingItems);
      if (repairResult.lastErrorMessage) {
        lastErrorMessage = repairResult.lastErrorMessage;
      }

      await new Promise((resolve) => {
        setTimeout(resolve, 160);
      });
    }

    const finalCart = await fetchCartState();
    const finalQtyMap = getVariantQtyMapFromCart(finalCart || { items: [] });
    const finalMissingItems = getMissingItemsFromCartDiff(items, beforeQtyMap, finalQtyMap);

    return {
      complete: finalMissingItems.length === 0,
      lastErrorMessage: lastErrorMessage || (finalMissingItems.length ? 'Some selected products could not be added.' : '')
    };
  }

  function showFeedback(section, message, type) {
    const feedback = section.querySelector('[data-fbt-feedback]');
    if (!feedback) return;

    if (!message) {
      feedback.textContent = '';
      feedback.classList.add('hidden');
      feedback.classList.remove('success', 'error');
      return;
    }

    feedback.textContent = message;
    feedback.classList.remove('hidden');
    feedback.classList.remove('success', 'error');
    feedback.classList.add(type === 'success' ? 'success' : 'error');
  }

  function getCheckedInputs(section) {
    return Array.from(section.querySelectorAll('.fbt-product-checkbox:checked'));
  }

  function getAddableInputs(section) {
    return getCheckedInputs(section).filter((input) => toInteger(input.dataset.variantId) > 0);
  }

  function getMainProductData(section) {
    return {
      variantId: toInteger(section.dataset.mainVariantId),
      price: toInteger(section.dataset.mainPrice),
      comparePrice: toInteger(section.dataset.mainComparePrice)
    };
  }

  function getOfferConfig(section) {
    return {
      offerTwoPrice: toInteger(section.dataset.offerTwoPrice),
      offerThreePrice: toInteger(section.dataset.offerThreePrice)
    };
  }

  function calculateBundleTotal(mainPrice, addonPrices, offerConfig) {
    const baseTotal = mainPrice + addonPrices.reduce((sum, price) => sum + toInteger(price), 0);
    const totalItems = 1 + addonPrices.length;

    if (offerConfig.offerThreePrice > 0 && totalItems >= 3) {
      const extraAddonTotal = addonPrices.slice(2).reduce((sum, price) => sum + toInteger(price), 0);
      return offerConfig.offerThreePrice + extraAddonTotal;
    }

    if (offerConfig.offerTwoPrice > 0 && totalItems >= 2) {
      const extraAddonTotal = addonPrices.slice(1).reduce((sum, price) => sum + toInteger(price), 0);
      return offerConfig.offerTwoPrice + extraAddonTotal;
    }

    return baseTotal;
  }

  function updateIncludingLine(section, selectedAddonCount) {
    const line = section.querySelector('[data-fbt-including-line]');
    if (!line) return;

    const mainTitle = section.dataset.mainTitle || 'this product';
    const itemWord = selectedAddonCount === 1 ? 'more item' : 'more items';
    line.textContent = `Including ${mainTitle} & Get ${selectedAddonCount} ${itemWord}`;
  }

  function resolveCurrentMainVariantId(section) {
    const productId = section.dataset.productId;
    const mainProduct = section.closest('.m-main-product') || document.querySelector(`.m-main-product[data-product-id="${productId}"]`);
    const selectors = [
      '.m-product-form--main [name="id"]',
      '.sticky-atc-form [name="id"]',
      'form[action*="/cart/add"] [name="id"]'
    ];

    for (const selector of selectors) {
      const input = mainProduct ? mainProduct.querySelector(selector) : document.querySelector(selector);
      const variantId = toInteger(input && input.value);
      if (variantId > 0) {
        section.dataset.mainVariantId = String(variantId);
        return variantId;
      }
    }

    return toInteger(section.dataset.mainVariantId);
  }

  function setButtonLoading(button, loading) {
    const defaultLabel = button.dataset.defaultLabel || button.textContent.trim();
    button.dataset.defaultLabel = defaultLabel;
    button.dataset.loading = loading ? 'true' : 'false';
    button.disabled = loading;
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    button.textContent = loading ? 'Adding...' : defaultLabel;
  }

  function updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll('.m-cart-count-bubble');
    cartCounts.forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove('m:hidden');
      } else {
        cartCount.classList.add('m:hidden');
      }
    });
  }

  async function renderCartDrawer(cartDrawer, cart) {
    if (!cartDrawer || !window.MinimogSettings || !window.MinimogSettings.routes) return;

    cartDrawer.classList.toggle('m-cart--empty', !cart || cart.item_count === 0);

    const response = await fetch(`${window.MinimogSettings.routes.cart}?section_id=cart-drawer`, {
      headers: {
        'X-SC-Internal': '1'
      }
    });

    const responseText = await response.text();
    const sections = typeof cartDrawer.getSectionsToRender === 'function' ? cartDrawer.getSectionsToRender() : [];

    sections.forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!sectionElement || typeof cartDrawer.getSectionInnerHTML !== 'function') return;
      sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(responseText, section.selector);
    });
  }

  async function refreshCartUI() {
    const cart = await fetchCartState();
    const cartDrawer = document.querySelector('m-cart-drawer');
    const hasDrawer = !!(window.MinimogSettings && window.MinimogSettings.enable_cart_drawer && cartDrawer);

    if (cart && typeof cart.item_count === 'number') {
      updateCartCount(cart.item_count);
    }

    if (hasDrawer) {
      await renderCartDrawer(cartDrawer, cart);
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }

    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, cart || { source: 'product-fbt' });
      if (!hasDrawer) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.openCartDrawer);
      }
    }
  }

  function updateFbtTotal(section) {
    const checkedInputs = getCheckedInputs(section);
    const addableInputs = getAddableInputs(section);
    const mainProduct = getMainProductData(section);
    const offerConfig = getOfferConfig(section);
    let baseTotalPrice = mainProduct.price;
    let compareTotalPrice = mainProduct.comparePrice;
    const addonPrices = addableInputs.map((input) => toInteger(input.dataset.productPrice));

    checkedInputs.forEach((input) => {
      baseTotalPrice += toInteger(input.dataset.productPrice);
      compareTotalPrice += toInteger(input.dataset.productComparePrice);
    });

    const discountedTotal = calculateBundleTotal(mainProduct.price, addonPrices, offerConfig);
    const compareDisplayPrice = Math.max(compareTotalPrice, baseTotalPrice);
    const totalPriceElement = section.querySelector('[data-fbt-total-price]');
    const comparePriceElement = section.querySelector('[data-fbt-total-compare]');
    const addToCartButton = section.querySelector('[data-fbt-add-to-cart]');
    const addableCount = addableInputs.length;

    if (totalPriceElement) {
      totalPriceElement.textContent = formatMoney(discountedTotal);
    }

    if (comparePriceElement) {
      if (compareDisplayPrice > discountedTotal) {
        comparePriceElement.classList.remove('hidden');
        comparePriceElement.textContent = formatMoney(compareDisplayPrice);
      } else {
        comparePriceElement.classList.add('hidden');
      }
    }

    updateIncludingLine(section, addableCount);

    if (addToCartButton && addToCartButton.dataset.loading !== 'true') {
      addToCartButton.disabled = addableCount === 0;
    }
  }

  async function addSelectedItemsToCart(section) {
    const button = section.querySelector('[data-fbt-add-to-cart]');
    if (!button) return;

    const selectedInputs = getAddableInputs(section);
    if (!selectedInputs.length) {
      showFeedback(section, 'Select at least one product to add.', 'error');
      updateFbtTotal(section);
      return;
    }

    const mainProduct = getMainProductData(section);
    mainProduct.variantId = resolveCurrentMainVariantId(section);

    if (!mainProduct.variantId) {
      showFeedback(section, 'Current product variant is unavailable. Please select a valid variant.', 'error');
      updateFbtTotal(section);
      return;
    }

    const itemMap = {};
    itemMap[mainProduct.variantId] = (itemMap[mainProduct.variantId] || 0) + 1;

    selectedInputs.forEach((input) => {
      const variantId = toInteger(input.dataset.variantId);
      if (variantId > 0) {
        itemMap[variantId] = (itemMap[variantId] || 0) + 1;
      }
    });

    const items = Object.keys(itemMap).map((variantId) => ({
      id: toInteger(variantId),
      quantity: itemMap[variantId]
    }));

    if (!items.length) {
      showFeedback(section, 'Linked variants are missing for selected products.', 'error');
      updateFbtTotal(section);
      return;
    }

    setButtonLoading(button, true);
    showFeedback(section, '', 'success');

    try {
      const cartDrawer = document.querySelector('m-cart-drawer');
      const useAjaxAtc = !window.MinimogSettings || window.MinimogSettings.use_ajax_atc !== false;
      const enableCartDrawer = !!(window.MinimogSettings && window.MinimogSettings.enable_cart_drawer && cartDrawer);
      const requestBody = { items };

      if (enableCartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        requestBody.sections = [...new Set(cartDrawer.getSectionsToRender().map((sectionToRender) => sectionToRender.id))];
        requestBody.sections_url = window.location.pathname;
      }

      const addResult = await requestCartAdd(requestBody);

      if (!addResult.response.ok || !addResult.payload) {
        throw new Error(getCartErrorMessage(addResult.payload, addResult.rawText, addResult.response.status));
      }

      if (!useAjaxAtc && window.MinimogSettings && window.MinimogSettings.routes) {
        window.location = window.MinimogSettings.routes.cart;
        return;
      }

      if (enableCartDrawer && addResult.payload.sections && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(addResult.payload);
      } else {
        await refreshCartUI();
      }

      if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, addResult.payload);
      }

      showFeedback(section, 'Selected products were added to cart.', 'success');
    } catch (error) {
      showFeedback(section, error && error.message ? error.message : 'Unable to add selected products.', 'error');
    } finally {
      setButtonLoading(button, false);
      updateFbtTotal(section);
    }
  }

  function initSection(section) {
    if (initializedSections.has(section)) return;
    initializedSections.add(section);

    const checkboxes = section.querySelectorAll('.fbt-product-checkbox');
    const addToCartButton = section.querySelector('[data-fbt-add-to-cart]');

    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        showFeedback(section, '', 'success');
        updateFbtTotal(section);
      });
    });

    if (addToCartButton) {
      addToCartButton.addEventListener('click', (event) => {
        event.preventDefault();
        addSelectedItemsToCart(section);
      });
    }

    if (window.MinimogEvents && typeof window.MinimogEvents.subscribe === 'function' && section.dataset.productId) {
      window.MinimogEvents.subscribe(`${section.dataset.productId}__VARIANT_CHANGE`, (variant) => {
        if (!variant || !variant.id) return;
        section.dataset.mainVariantId = String(variant.id);
        section.dataset.mainPrice = String(toInteger(variant.price));
        section.dataset.mainComparePrice = String(toInteger(variant.compare_at_price));
        updateFbtTotal(section);
      });
    }

    updateFbtTotal(section);
  }

  function initFbtSections(root = document) {
    root.querySelectorAll('[data-fbt-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFbtSections());
  } else {
    initFbtSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initFbtSections(event.target);
  });
})();
